import { ContextMenuInputPoint } from '@thoughtspot/chart-common';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { assign } from '@thoughtspot/string-util';
import { isAttribute } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getAxisConfig, getChartType, getLegendColumns, getXAxisColumns, getYAxisColumns, } from '../core/chart-model-utils';
import { getName } from '../core/column-name-util';
import { GenericMenuItemType, getRawValueForCustomCalendar, } from '../core/context-menu-point-utils';
import { containsMeasureNamesOrMeasureValuesColumns, findMeasureNameColumnIndex, isMeasureNameColumn, } from '../core/measure-names-and-values';
import { createLogger } from '../utils/logger';
import { clearSortOperation, sortOperationOnPivotGrid, toggleHeaderExpansionState, toggleSelectionExpansionState, } from './components/pivot.component.utils';
import { getMeasureValuesColumns, getMeasureValuesColumnsByName, } from './model/pivot-data-model-utils';
export const ROW = 'row';
export const COLUMN = 'column';
export const DATA = 'data';
const logger = createLogger('pivot-context-menu-util');
export function getPivotExpandMenuItems(pivotEvent, { chartModel, pivotDataModel, pivotInstance, getTranslatedText, eventsMap, }, openContextMenuOnLeftClick, fetchPivotSummaryFromBackend) {
    var _a;
    if (pivotEvent.area === DATA) {
        return [];
    }
    let columnIds;
    if (pivotEvent.area === ROW) {
        columnIds = getXAxisColumns(chartModel).map(col => col.id);
    }
    else {
        columnIds = getLegendColumns(chartModel).map(col => col.id);
    }
    if (columnIds.length === 1) {
        return [];
    }
    if (!((_a = pivotEvent === null || pivotEvent === void 0 ? void 0 : pivotEvent.cell) === null || _a === void 0 ? void 0 : _a.path) ||
        columnIds.length === pivotEvent.cell.path.length) {
        return [];
    }
    const menuItems = [];
    menuItems.push({
        id: GenericMenuItemType.SEPARATOR,
        label: '',
        icon: null,
        onClick: _.noop,
    });
    if (openContextMenuOnLeftClick) {
        menuItems.push({
            id: GenericMenuItemType.PIVOT_SELECTION_EXPAND,
            label: getTranslatedText('pivotTable.EXPAND_SELECTION'),
            icon: IconID.EXPAND,
            onClick: () => {
                pivotDataModel.setClientStateUpdatedBySystemAction(false);
                toggleSelectionExpansionState(pivotEvent, {
                    pivotInstance,
                    chartModel,
                    eventsMap,
                    pivotDataModel,
                }, true, fetchPivotSummaryFromBackend);
            },
        });
        menuItems.push({
            id: GenericMenuItemType.PIVOT_SELECTION_COLLAPSE,
            label: getTranslatedText('pivotTable.COLLAPSE_SELECTION'),
            icon: IconID.EXPAND,
            onClick: () => {
                pivotDataModel.setClientStateUpdatedBySystemAction(false);
                toggleSelectionExpansionState(pivotEvent, {
                    pivotInstance,
                    chartModel,
                    pivotDataModel,
                }, false, fetchPivotSummaryFromBackend);
            },
        });
    }
    menuItems.push({
        id: GenericMenuItemType.PIVOT_HEADER_EXPAND,
        label: getTranslatedText('pivotTable.EXPAND_LEVEL'),
        icon: IconID.EXPAND,
        onClick: () => {
            pivotDataModel.setClientStateUpdatedBySystemAction(false);
            toggleHeaderExpansionState(columnIds[pivotEvent.cell.path.length - 1], pivotEvent.area, pivotEvent.cell.path.length, true, { pivotInstance, chartModel, eventsMap, pivotDataModel });
            collectEvent(new UserAction(userActionTypeEnum.PIVOT_CONTEXT_EXPAND_ALL), {
                chartType: getChartType(chartModel),
            });
        },
    });
    menuItems.push({
        id: GenericMenuItemType.PIVOT_HEADER_COLLAPSE,
        label: getTranslatedText('pivotTable.COLLAPSE_LEVEL'),
        icon: IconID.EXPAND,
        onClick: () => {
            pivotDataModel.setClientStateUpdatedBySystemAction(false);
            toggleHeaderExpansionState(columnIds[pivotEvent.cell.path.length - 1], pivotEvent.area, pivotEvent.cell.path.length, false, { pivotInstance, chartModel, pivotDataModel });
            collectEvent(new UserAction(userActionTypeEnum.PIVOT_CONTEXT_COLLAPSE_ALL), {
                chartType: getChartType(chartModel),
            });
        },
    });
    return menuItems;
}
export function getPivotSortMenuItems(pivotEvent, { chartModel, pivotInstance, getTranslatedText, pivotDataModel, }) {
    if (pivotEvent.area === DATA) {
        return [];
    }
    let columnDataColumns;
    let areaColumns;
    if (pivotEvent.area === ROW) {
        areaColumns = getXAxisColumns(chartModel);
        columnDataColumns = getLegendColumns(chartModel);
    }
    else {
        areaColumns = getLegendColumns(chartModel);
        columnDataColumns = getXAxisColumns(chartModel);
    }
    const containsMeasureNames = containsMeasureNamesOrMeasureValuesColumns(getAxisConfig(chartModel));
    const containsMeasureNamesInCurrentArea = areaColumns.some(col => isMeasureNameColumn(col));
    const measureNameIndex = findMeasureNameColumnIndex(areaColumns);
    if (containsMeasureNames) {
        if (!containsMeasureNamesInCurrentArea) {
            return [];
        }
        if (measureNameIndex !== -1 && measureNameIndex > pivotEvent.rowIndex) {
            return [];
        }
    }
    const sortMenuItems = [];
    const dataColumns = pivotDataModel.measures;
    columnDataColumns.forEach(column => {
        dataColumns.forEach(dataColumn => {
            sortMenuItems.push({
                id: GenericMenuItemType.PIVOT_SORT,
                label: assign(pivotEvent.area === COLUMN
                    ? getTranslatedText('pivotTable.SORT_COLUMN', {
                        skipInterpolation: true,
                    })
                    : getTranslatedText('pivotTable.SORT_ROW', {
                        skipInterpolation: true,
                    }), [
                    `${getName(column)}${dataColumns.length > 1
                        ? ` - ${pivotDataModel.getFieldCaption(dataColumn)}`
                        : ''}`,
                ]),
                icon: IconID.SORT,
                onClick: () => {
                    pivotDataModel.setClientStateUpdatedBySystemAction(false);
                    sortOperationOnPivotGrid(column.id, pivotEvent.cell.path, dataColumn.id, { pivotInstance });
                    collectEvent(new UserAction(userActionTypeEnum.PIVOT_CONTEXT_SORT), {
                        pivotColumnId: column.id,
                        sortPath: pivotEvent.cell.path,
                        dataColumnId: dataColumn.id,
                        chartType: getChartType(chartModel),
                    });
                },
            });
        });
    });
    if (sortMenuItems.length > 0) {
        sortMenuItems.unshift({
            id: GenericMenuItemType.SEPARATOR,
            label: '',
            icon: null,
            onClick: _.noop,
        });
        sortMenuItems.push({
            id: GenericMenuItemType.PIVOT_CLEAR_SORT,
            label: getTranslatedText('pivotTable.REMOVE_ALL_SORTING'),
            icon: IconID.SORT,
            onClick: () => {
                pivotDataModel.setClientStateUpdatedBySystemAction(false);
                clearSortOperation({ chartModel, pivotInstance });
                collectEvent(new UserAction(userActionTypeEnum.PIVOT_CONTEXT_CLEAR_SORT), {
                    chartType: getChartType(chartModel),
                });
            },
        });
    }
    return sortMenuItems;
}
export const getSageColumnFromMeasureName = (rowDataColumns, columnDataColumns, selectedCell, chartModel, pivotEvent) => {
    const rowIndex = findMeasureNameColumnIndex(rowDataColumns);
    const columnIndex = findMeasureNameColumnIndex(columnDataColumns);
    const isMeasureNameColumnPresentInRow = rowIndex !== -1;
    const measureNameIndex = isMeasureNameColumnPresentInRow
        ? rowIndex
        : columnIndex;
    if (measureNameIndex === -1) {
        throw new Error('Measure Names column not found in either rowDataColumns or columnDataColumns');
    }
    let selectedPath;
    switch (pivotEvent.area) {
        case ROW:
        case COLUMN:
            selectedPath = selectedCell.path;
            break;
        case DATA:
            selectedPath = isMeasureNameColumnPresentInRow
                ? selectedCell.rowPath
                : selectedCell.columnPath;
            break;
        default:
            throw new Error('Invalid pivot event area');
    }
    const measureValuesColumns = getMeasureValuesColumns(chartModel);
    const selectedMeasureValueColumns = getMeasureValuesColumnsByName(chartModel, selectedPath[measureNameIndex]);
    return selectedMeasureValueColumns.length > 0
        ? selectedMeasureValueColumns
        : measureValuesColumns;
};
export const isMeasureNameColumnPresent = (rowDataColumns, columnDataColumns) => {
    return [rowDataColumns, columnDataColumns].some(col => findMeasureNameColumnIndex(col) !== -1);
};
export const addSelectedSageColumnToClickedPoint = (pivotEvent, rowDataColumns, columnDataColumns, selectedCell, model, clickedPoint) => {
    if (pivotEvent.area === DATA) {
        return;
    }
    const sageColumn = getSageColumnFromMeasureName(rowDataColumns, columnDataColumns, selectedCell, model, pivotEvent);
    clickedPoint.pushSelected({
        column: sageColumn[0],
        value: null,
    });
};
export function getClickedContextMenuInputPoint(model, pivotEvent, options) {
    const selectedCell = pivotEvent.cell;
    const { customCalendarMap } = options;
    const clickedPoint = new ContextMenuInputPoint();
    const rowDataColumns = getXAxisColumns(model);
    const columnDataColumns = getLegendColumns(model);
    const dataColumns = getYAxisColumns(model);
    let rowPath = [];
    let columnPath = [];
    if (pivotEvent.area === ROW) {
        rowPath = selectedCell.path || [];
    }
    else if (pivotEvent.area === COLUMN) {
        columnPath = selectedCell.path || [];
    }
    else if (pivotEvent.area === DATA) {
        rowPath = selectedCell.rowPath || [];
        columnPath = selectedCell.columnPath || [];
    }
    rowPath.forEach((rowValue, rowIndex) => {
        let cellValue = rowValue;
        const rowColumn = rowDataColumns[rowIndex];
        if (isMeasureNameColumn(rowColumn)) {
            addSelectedSageColumnToClickedPoint(pivotEvent, rowDataColumns, columnDataColumns, selectedCell, model, clickedPoint);
        }
        else {
            cellValue = getRawValueForCustomCalendar(customCalendarMap === null || customCalendarMap === void 0 ? void 0 : customCalendarMap[rowColumn.id], rowColumn, rowValue);
            clickedPoint.pushSelected({
                column: rowColumn,
                value: cellValue,
            });
        }
    });
    columnPath.forEach((columnValue, columnIndex) => {
        const columnDataColumn = columnDataColumns[columnIndex];
        if (isMeasureNameColumn(columnDataColumn)) {
            addSelectedSageColumnToClickedPoint(pivotEvent, rowDataColumns, columnDataColumns, selectedCell, model, clickedPoint);
        }
        else {
            const cellValue = getRawValueForCustomCalendar(customCalendarMap === null || customCalendarMap === void 0 ? void 0 : customCalendarMap[columnDataColumn === null || columnDataColumn === void 0 ? void 0 : columnDataColumn.id], columnDataColumn, columnValue);
            clickedPoint.pushSelected({
                column: columnDataColumn,
                value: cellValue,
            });
        }
    });
    const columns = [];
    if (isMeasureNameColumnPresent(rowDataColumns, columnDataColumns)) {
        const sageColumn = getSageColumnFromMeasureName(rowDataColumns, columnDataColumns, selectedCell, model, pivotEvent);
        columns.push(...sageColumn);
    }
    else {
        columns.push(...dataColumns);
    }
    columns.forEach((dataColumn, dataColumnIndex) => {
        if (selectedCell.dataIndex === dataColumnIndex) {
            if (_.isArray(selectedCell.value)) {
                selectedCell.value.forEach(val => {
                    clickedPoint.pushSelected({
                        column: dataColumn,
                        value: val,
                    });
                });
            }
            else {
                clickedPoint.pushSelected({
                    column: dataColumn,
                    value: selectedCell.value,
                });
            }
        }
        else if (!isAttribute(dataColumn)) {
            clickedPoint.pushDeselected({
                column: dataColumn,
                value: null,
            });
        }
    });
    return clickedPoint;
}
//# sourceMappingURL=pivot-context-menu.utils.js.map